import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Cart toggle for the paid gift bag plus two free photo frames.
 * @extends {Component}
 */
class CartGiftBundle extends Component {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    this.#checkbox = this.querySelector('.cart-gift-bundle__checkbox');
    this.#checkbox?.addEventListener('change', this.#handleToggle, { signal });
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #controller = new AbortController();
  #checkbox = null;
  #busy = false;

  #handleToggle = async () => {
    if (this.#busy) return;
    const turnOn = this.#checkbox instanceof HTMLInputElement && this.#checkbox.checked;
    this.#busy = true;
    if (this.#checkbox instanceof HTMLInputElement) this.#checkbox.disabled = true;
    this.#clearError();

    try {
      if (turnOn) await this.#addBundle();
      else await this.#removeBundle();
    } finally {
      this.#busy = false;
      if (this.#checkbox instanceof HTMLInputElement) this.#checkbox.disabled = false;
    }
  };

  #getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  async #refresh(data) {
    this.#getSectionIds().forEach((sectionId) => {
      if (data.sections?.[sectionId]) morphSection(sectionId, data.sections[sectionId]);
    });
  }

  #showError(message) {
    const error = this.querySelector('[ref="errorMessage"]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }

  #clearError() {
    const error = this.querySelector('[ref="errorMessage"]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = '';
    error.classList.add('hidden');
  }

  async #addBundle() {
    const bagId = Number(this.dataset.bagVariantId);
    const frameId = Number(this.dataset.frameVariantId);
    const frameQty = Number(this.dataset.frameQuantity) || 2;
    const linked = this.dataset.linkedVariantId || '';
    if (!bagId) return;

    const linkedProp = linked ? { _linked_variant: linked } : {};
    const items = [{ id: bagId, quantity: 1, properties: linkedProp }];
    if (frameId) {
      items.push({
        id: frameId,
        quantity: frameQty,
        properties: { _free_gift: 'true', ...linkedProp },
      });
    }

    const sections = this.#getSectionIds();
    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const data = await response.json();

    if (data.status) {
      this.#showError(data.message || data.description || 'Unable to add the gift bundle.');
      if (this.#checkbox instanceof HTMLInputElement) this.#checkbox.checked = false;
      return;
    }

    await this.#refresh(data);
  }

  async #removeBundle() {
    const updates = {};
    if (this.dataset.bagLineKey) updates[this.dataset.bagLineKey] = 0;
    if (this.dataset.frameLineKey) updates[this.dataset.frameLineKey] = 0;
    if (Object.keys(updates).length === 0) return;

    const sections = this.#getSectionIds();
    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({
          updates,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const data = await response.json();

    if (data.status) {
      this.#showError(data.message || data.description || 'Unable to remove the gift bundle.');
      if (this.#checkbox instanceof HTMLInputElement) this.#checkbox.checked = true;
      return;
    }

    await this.#refresh(data);
  }

  /** Remove gift lines when their linked pack is no longer in the cart. */
  #handleCartUpdate = async (event) => {
    if (event.detail?.data?.source === 'cart-gift-bundle') return;
    if (event.detail?.data?.source !== 'cart-items-component') return;

    const cart = event.detail?.resource;
    if (!cart?.items) return;

    const variantIds = new Set(cart.items.map((item) => String(item.variant_id)));
    const updates = {};
    for (const item of cart.items) {
      const linked = item.properties?._linked_variant;
      if (linked && !variantIds.has(String(linked))) updates[item.key] = 0;
    }
    if (Object.keys(updates).length === 0) return;

    const sections = this.#getSectionIds();
    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({
          updates,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const data = await response.json();
    if (!data.token) return;

    await this.#refresh(data);
    document.dispatchEvent(
      new CartUpdateEvent(data, '', { source: 'cart-gift-bundle', sections: data.sections })
    );
  };
}

if (!customElements.get('cart-gift-bundle')) {
  customElements.define('cart-gift-bundle', CartGiftBundle);
}
